// Машинерия построения canvas-иконок POI (спрайты карты + чипы кластеров) -
// вынесена из MapView.vue (см. пояснение в constants.ts этой же папки).
//
// poiClusterIconDataUrlCache/poiClusterIconDataUrl/createPoiClusterChipElement
// перенесены сюда вместе с остальным - они не читают `map`/реактивное
// состояние компонента (только POI_ICON_SVG_PATHS/POI_COLOR ниже и
// document/canvas API), поэтому чистые в том же смысле, что и остальной
// файл, несмотря на то, что их единственный вызывающий (refreshPoiClusterMarkers)
// остаётся реактивным кодом в MapView.vue.

// POI монохромны. Раскраска по `kind` (вершина коричневая, родник синий,
// хижина оранжевая...) - не то, как устроена топокарта: OpenTopoMap
// различает POI ИКОНКОЙ, а цвет резервирует для рельефа (изолинии, вода,
// растительность). Радужные точки поверх коричневого поля изолиний тоже
// читаются как дата-вижуализация, а не карта.
//
// Воспроизводить набор иконок OTM буквально не входит в задачу и было бы
// подделкой - это их PNG-спрайты, нарисованные вручную, и наполовину похожий
// набор хуже честного нейтрального. Поэтому: один тёмный цвет для всех
// обычных пиктограмм (pois-icon), плюс два исключения - вершины и перевалы
// сохраняют собственную форму (универсальная конвенция топокарт, без неё
// туристической карте не обойтись), а спасслужбы/вода теперь пины, не
// глифы вовсе (см. POI_PIN_VARIANT в MapView.vue).
export const POI_COLOR = "#3a3733";

export const PEAK_ICON_IMAGE_ID = "otm-peak-triangle";
export const PASS_ICON_IMAGE_ID = "otm-pass-bowtie";

// Треугольник вершины, рисуется один раз в ImageData при 2x и отдаётся в
// map.addImage - тот же путь, что и у buildArrowIconImage для стрелок
// маршрута: в проекте нет спрайт-листа, и заводить сборку спрайта ради одного
// 16px глифа не стоит. Тёмная заливка с белой обводкой, чтобы не терялся
// поверх изолинии.
// Вершина - горка из двух пиков (большой со снежной шапкой и меньший
// справа) в том же графитовом цвете, что и перевал (POI_COLOR), - читается на
// любой подложке. Светлая тема - тёмная с белой обводкой, тёмная - светлая с
// тёмной обводкой.
export const PEAK_ICON_DARK_IMAGE_ID = "otm-peak-mountain-dark";

const PEAK_PALETTES = {
  light: { back: "#6b6660", front: POI_COLOR, cap: "#f4f2ee", edge: "#ffffff" },
  dark: { back: "#aeb3ba", front: "#e7e9ec", cap: "#ffffff", edge: "#13171c" },
};

export const buildPeakIconImage = (theme: "light" | "dark" = "light"): ImageData | null => {
  const w = 36; // 18 CSS px при pixelRatio 2
  const h = 30;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const c = PEAK_PALETTES[theme];
  const poly = (pts: [number, number][], fill: string, stroke: boolean) => {
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
    ctx.fillStyle = fill;
    if (stroke) {
      ctx.lineWidth = 2.6;
      ctx.lineJoin = "round";
      ctx.strokeStyle = c.edge;
      ctx.stroke();
    }
    ctx.fill();
  };
  // меньший пик справа (сзади), большой слева (впереди), шапка поверх большого
  poly([[14, 26], [25, 10], [34, 26]], c.back, true);
  poly([[2, 26], [15, 4], [28, 26]], c.front, true);
  poly([[15, 4], [20.2, 12.7], [17.4, 11.4], [15, 13.6], [12.6, 11.4], [9.8, 12.7]], c.cap, false);
  return ctx.getImageData(0, 0, w, h);
};

// Ещё две формы по той же логике, что и у треугольника вершины: своя форма
// нужна там, где одной подписи недостаточно.
//
//   перевал  - «бабочка»: два склона, сходящиеся к седловине - стандартное
//              изображение седловины на топокарте, читается уже при 10px.
//              Перевал - самая важная точка маршрута (решает, пройдёт ли
//              маршрут вообще), и их 360 внутри MAP_MAX_BOUNDS против 872
//              вершин - слишком много, чтобы оставлять анонимными точками.
//   спасслужба - крест, универсальный знак помощи. Во всём экстракте ровно
//              один узел `emergency=mountain_rescue`, так что это не стоит
//              ничего и это единственный символ, который хочется найти не
//              читая.
//
// Эти два остаются нарисованными вручную (готового глифа под «бабочку»/крест
// нет); у всех остальных видов теперь тоже настоящая пиктограмма - см.
// POI_ICON_SVG_PATHS ниже.
export const buildIconImage = (draw: (ctx: CanvasRenderingContext2D, size: number) => void) => {
  const size = 24; // 12 CSS px при pixelRatio 2, как у треугольника вершины
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  draw(ctx, size);
  return ctx.getImageData(0, 0, size, size);
};

// Сначала обводка, потом заливка, как у треугольника: широкая белая обводка
// рисуется первой, затем половина перекрывается заливкой - ровный ореол без
// второго пути.
export const strokeAndFill = (ctx: CanvasRenderingContext2D) => {
  ctx.fillStyle = POI_COLOR;
  ctx.fill();
  ctx.lineWidth = 2.4;
  ctx.lineJoin = "round";
  ctx.strokeStyle = "#ffffff";
  ctx.stroke();
  ctx.fill();
};

export const buildPassIconImage = () =>
  buildIconImage((ctx, size) => {
    const inset = 2.5;
    const mid = size / 2;
    // Левый склон вниз к седловине, затем правый вверх - один путь, чтобы
    // обводка шла вокруг всей фигуры, а не между половинками.
    ctx.beginPath();
    ctx.moveTo(inset, inset);
    ctx.lineTo(mid, mid);
    ctx.lineTo(size - inset, inset);
    ctx.lineTo(size - inset, size - inset);
    ctx.lineTo(mid, mid);
    ctx.lineTo(inset, size - inset);
    ctx.closePath();
    strokeAndFill(ctx);
  });

// Пиктограммы для пятнадцати видов, раньше делившие одну общую точку (см.
// комментарий про перевал/спасслужбу выше) - обведены по Osmic
// (github.com/gmgeo/osmic, CC0), выбран после сравнения с Maki+Temaki на
// вкладке Sandbox → «Стиль карты». Один вид, для которого у Osmic нет иконки
// (firepit), обведён по Temaki (github.com/rapideditor/temaki, тоже CC0);
// остальное - Osmic. waste_disposal переиспользует иконку waste_basket -
// Osmic их тоже не различает.
//
// Зарегистрированы с `sdf: true` (addPoiIcon в basemapLayers.ts), поэтому
// один чёрно-белый битмап на вид можно тонировать через icon-color при
// рендере (paint слоя pois-icon ставит его в сплошной POI_COLOR - водные
// виды, которые раньше красились в синий, теперь пины и через этот слой не
// рисуются).

// Одна простая капля для всех водных видов (см. запись каждого вида ниже) -
// не пять разных силуэтов ради различия (родник/кран/колодец), которое эта
// карта визуально не кодирует - подпись и так говорит, что это. Обведена по
// капле с svgrepo.com (scratch/glyphs/droplet-svgrepo-com.svg): её исходный
// viewBox "0 0 8 8" с `transform="translate(1)"` на path, а
// Path2D/buildSvgPathIconImage трансформы не поддерживает (читает только
// сырую строку `d`), поэтому сдвиг +1 по x запечён прямо в координатах -
// исходное `M3 0` стало `M4 0`, остальные команды уже относительные и правки
// не требовали.
export const WATER_DROPLET_VIEWBOX = 8;
export const WATER_DROPLET_PATH =
  "M4 0l-.34.34c-.11.11-2.66 2.69-2.66 4.88 0 1.65 1.35 3 3 3s3-1.35 3-3c0-2.18-2.55-4.77-2.66-4.88l-.34-.34zm-1.5 4.72c.28 0 .5.22.5.5 0 .55.45 1 1 1 .28 0 .5.22.5.5s-.22.5-.5.5c-1.1 0-2-.9-2-2 0-.28.22-.5.5-.5z";

export const POI_ICON_SVG_PATHS: Record<
  string,
  // originX/originY - компенсирует ненулевой min-x/min-y исходного viewBox
  // через ctx.translate() в buildSvgPathIconImage, вместо правки координат
  // в строке пути. 0,0 если исходный viewBox уже с нуля.
  {
    viewBox: number;
    paths: string[];
    strokeWidth?: number;
    originX?: number;
    originY?: number;
    // Правило заливки вырезов (взято из исходного SVG 1:1) - default nonzero.
    fillRule?: CanvasFillRule;
  }
> = {
  camp_site: {
    viewBox: 14,
    paths: [
      "M 14,14 8.000001,2.9999992 9.000001,0 l -1,0 -1,1.9999995 L 6,0 5,0 6,2.9999992 0,14 z m -6.999999,-7.0000008 3,6.0000018 -6.000001,0 z",
    ],
  },
  alpine_hut: {
    viewBox: 14,
    paths: [
      "M 7 1 L 0 5 L 0 7 L 1.5 6.3496094 L 1.5 14 L 3 14 L 3 5.6992188 L 7 4 L 11 5.6992188 L 11 14 L 12.5 14 L 12.5 6.3496094 L 14 7 L 14 5 L 7 1 z M 5.5 6 C 4.9285714 6 4 7.2857142 4 9 C 4 10.714286 5 11 5 11 L 5 14 L 6 14 L 6 11 C 6 11 7 10.714286 7 9 C 7 7.2857142 6.0714286 6 5.5 6 z M 8 6 L 8 14 L 9 14 L 9 11 L 10 11 L 10 10 C 10 8 9 6 8 6 z",
    ],
  },
  // Спасхижина - тот же домик, что у альпхижины (свой вид нужен ради пина и подписи).
  rescue_hut: {
    viewBox: 14,
    paths: [
      "M 7 1 L 0 5 L 0 7 L 1.5 6.3496094 L 1.5 14 L 3 14 L 3 5.6992188 L 7 4 L 11 5.6992188 L 11 14 L 12.5 14 L 12.5 6.3496094 L 14 7 L 14 5 L 7 1 z M 5.5 6 C 4.9285714 6 4 7.2857142 4 9 C 4 10.714286 5 11 5 11 L 5 14 L 6 14 L 6 11 C 6 11 7 10.714286 7 9 C 7 7.2857142 6.0714286 6 5.5 6 z M 8 6 L 8 14 L 9 14 L 9 11 L 10 11 L 10 10 C 10 8 9 6 8 6 z",
    ],
  },
  wilderness_hut: {
    viewBox: 14,
    paths: [
      "M 8,0 8,2 7,1.5 0,5 0,7 1.5,6.3496094 1.5,14 3,14 3,5.6992188 7,4 8,4.4257812 8,9 4.5,9 c 0.025314,1.65157 -0.034277,3.38952 0,5 l 5,0 C 9.518206,11.046736 9.50603,8.0503867 9.5,5.0625 L 11,5.6992188 11,14 12.5,14 12.5,6.3496094 14,7 14,5 9.5,2.75 C 9.4984477,1.8314022 9.5,0.9158261 9.5,0 L 8,0 Z M 7,11 c 0,0 1,0 1,1 l 0,1 -2,0 0,-1 c 0,-1 1,-1 1,-1 z",
    ],
  },
  // Бинокль - svgrepo.com иконка, использована как есть (один абсолютный
  // путь, трансформа нет).
  viewpoint: {
    viewBox: 512,
    paths: [
      "M510.868,327.816c-0.61-4.281-1.471-8.474-2.528-12.572l0.733-0.095c0,0-42.276-112.08-51.077-151.655c-7.37-29.951-28.899-53.445-56.26-64.216c-0.697-2.495-1.275-4.744-1.706-6.664c-5.963-24.275-29.473-40.554-54.752-36.975c-25.276,3.58-43.345,25.75-42.336,50.727c0.203,3.229,0.466,7.382,0.765,12.102c-18.842,17.961-29.939,43.56-28.831,70.96c0.047,0.765,0.096,1.546,0.143,2.359h-38.042c0.047-0.813,0.095-1.594,0.148-2.359c1.104-27.401-9.986-52.999-28.836-70.96c0.299-4.719,0.563-8.873,0.766-12.102c1.008-24.977-17.057-47.147-42.337-50.727c-25.276-3.58-48.789,12.7-54.752,36.975c-0.43,1.921-1.008,4.169-1.706,6.664c-27.36,10.77-48.889,34.265-56.26,64.216C45.199,203.069,2.923,315.149,2.923,315.149l0.733,0.095c-1.056,4.098-1.917,8.291-2.523,12.572c-8.765,61.872,34.296,119.143,96.168,127.905c61.872,8.761,119.14-34.296,127.901-96.168c0.606-4.289,0.94-8.546,1.064-12.779l0.733,0.112c0,0,0.195-3.029,0.538-8.267h56.922c0.343,5.238,0.538,8.267,0.538,8.267l0.734-0.112c0.124,4.233,0.458,8.49,1.064,12.779c8.762,61.872,66.029,104.929,127.901,96.168C476.576,446.96,519.629,389.688,510.868,327.816z M187.861,354.268c-5.848,41.24-44.022,69.955-85.274,64.112c-41.248-5.844-69.952-44.031-64.108-85.27c5.84-41.248,44.018-69.956,85.266-64.113C164.996,274.842,193.7,313.012,187.861,354.268z M409.41,418.38c-41.252,5.843-79.426-22.872-85.274-64.112c-5.839-41.256,22.864-79.426,64.116-85.271c41.252-5.843,79.426,22.864,85.267,64.113C479.362,374.35,450.658,412.537,409.41,418.38z",
    ],
  },
  shelter: {
    viewBox: 14,
    paths: [
      "M 3.53125 0 L 2.53125 2 L 3 2 L 4.046875 0 L 3.53125 0 z M 7.53125 0 L 6.53125 2 L 7 2 L 8.046875 0 L 7.53125 0 z M 11.53125 0 L 10.53125 2 L 11 2 L 12.046875 0 L 11.53125 0 z M 1.0234375 1 L 0.0234375 3 L 0.4921875 3 L 1.5410156 1 L 1.0234375 1 z M 5.03125 1 L 4.03125 3 L 4.5 3 L 5.546875 1 L 5.03125 1 z M 9.03125 1 L 8.03125 3 L 8.5 3 L 9.546875 1 L 9.03125 1 z M 13.03125 1 L 12.03125 3 L 12.5 3 L 13.546875 1 L 13.03125 1 z M 3 4 L 0 7 L 1.5 7 L 1.5 14 L 3 14 L 3 7 L 11 7 L 11 14 L 12.5 14 L 12.5 7 L 14 7 L 11 4 L 3 4 z",
    ],
  },
  // На горной карте важен сам факт "здесь есть вода", а не различие
  // OSM-подтипов - все пять водных видов используют одну и ту же константу
  // WATER_DROPLET_PATH; какой это подтип на самом деле, всё ещё говорит
  // подпись под pois-label (и попап).
  drinking_water: { viewBox: WATER_DROPLET_VIEWBOX, paths: [WATER_DROPLET_PATH] },
  water_tap: { viewBox: WATER_DROPLET_VIEWBOX, paths: [WATER_DROPLET_PATH] },
  water_point: { viewBox: WATER_DROPLET_VIEWBOX, paths: [WATER_DROPLET_PATH] },
  // scratch/glyphs/wc-sign-svgrepo-com.svg, viewBox уже "0 0 24 24".
  toilets: {
    viewBox: 24,
    paths: [
      "M9.80469 13.7734L10.8186 7.05043C10.8555 6.80585 11.0657 6.625 11.3131 6.625H12.5595C12.8706 6.625 13.1062 6.9062 13.0517 7.21257L11.2062 17.5876C11.1637 17.8262 10.9563 18 10.7139 18H9.23878C9.0001 18 8.7947 17.8313 8.74831 17.5972L7.5 11.2969L6.26659 17.5961C6.22065 17.8307 6.01501 18 5.77591 18H4.29362C4.05139 18 3.84399 17.8264 3.80141 17.5879L1.94873 7.2129C1.89401 6.90643 2.12963 6.625 2.44095 6.625H3.70217C3.94969 6.625 4.15996 6.8061 4.19665 7.05088L5.20312 13.7656L6.46272 7.03305C6.50698 6.79647 6.71351 6.625 6.95419 6.625H8.05354C8.29426 6.625 8.50081 6.79652 8.54503 7.03315L9.80469 13.7734Z",
      "M20.7068 14.2109C20.9916 14.2109 21.2212 14.4491 21.1879 14.732C21.0651 15.7739 20.7144 16.582 20.1356 17.1562C19.4637 17.8229 18.5158 18.1562 17.2919 18.1562C16.0054 18.1562 15.0184 17.7344 14.3309 16.8906C13.6486 16.0417 13.3075 14.8333 13.3075 13.2656V11.3516C13.3075 9.78906 13.6617 8.58594 14.37 7.74219C15.0783 6.89323 16.0627 6.46875 17.3231 6.46875C18.5627 6.46875 19.5028 6.8151 20.1434 7.50781C20.6978 8.10246 21.0469 8.91971 21.1906 9.95956C21.2303 10.2464 20.9992 10.4922 20.7097 10.4922H19.4275C19.1564 10.4922 18.9378 10.2757 18.9121 10.0059C18.8604 9.46252 18.7448 9.07255 18.5653 8.83594C18.3414 8.52865 17.9273 8.375 17.3231 8.375C16.7085 8.375 16.2736 8.59115 16.0184 9.02344C15.7632 9.45052 15.6278 10.1562 15.6122 11.1406V13.2891C15.6122 14.4193 15.7372 15.1953 15.9872 15.6172C16.2424 16.0391 16.6773 16.25 17.2919 16.25C17.896 16.25 18.3127 16.1042 18.5419 15.8125C18.7201 15.5816 18.8385 15.2073 18.897 14.6897C18.9271 14.4232 19.1444 14.2109 19.4126 14.2109H20.7068Z",
    ],
  },
  // scratch/glyphs/recycle-svgrepo-com.svg - исходный viewBox "-6.7 0 122.88
  // 122.88" (originX), стрелки переработки требуют fillRule: evenodd.
  waste_basket: {
    viewBox: 122.88,
    originX: -6.7,
    fillRule: "evenodd",
    paths: [
      "M2.35,9.63h38.3V3.76C40.64,1.69,42.33,0,44.4,0h21.14c2.07,0,3.76,1.69,3.76,3.76v5.87h37.83 c1.29,0,2.35,1.06,2.35,2.35V23.5H0V11.98C0,10.69,1.05,9.63,2.35,9.63L2.35,9.63z M8.69,29.61h92.92c1.94,0,3.7,1.6,3.52,3.52 l-7.86,86.23c-0.18,1.93-1.59,3.52-3.52,3.52l-77.3,0c-1.93,0-3.35-1.59-3.52-3.52L5.17,33.13C4.99,31.2,6.75,29.61,8.69,29.61 L8.69,29.61L8.69,29.61z M33.93,95.11l-6.16-10.59c-1.11-1.92-1.53-3.42-0.6-5.64l3.62-6.09l-3.63-1.95l12.17-0.05l6.07,10.61 l-3.75-2.15l-6.08,10.78c-0.58,1.02-1.06,1.8-1.35,2.96C34.05,93.7,33.96,94.41,33.93,95.11L33.93,95.11z M36.38,62.36l5.86-10.2 c1.65-2.05,3.7-2.79,5.65-2.24c1.68,0.48,2.15,1.23,3.04,2.6c1.07,1.63,2,3.37,2.98,5.08l-6.55,11.26L36.38,62.36L36.38,62.36z M49.71,48.43l12.26-0.04c2.22-0.01,3.73,0.39,5.18,2.3l3.46,6.18l3.51-2.17l-6.04,10.56l-12.23-0.05l3.74-2.17l-6.3-10.66 c-0.6-1.01-1.03-1.81-1.89-2.65C50.88,49.23,50.31,48.81,49.71,48.43L49.71,48.43z M76.4,67.42l5.9,10.17 c0.95,2.45,0.57,4.6-0.89,6.01c-1.25,1.22-2.14,1.24-3.77,1.34c-1.95,0.11-3.92,0.05-5.89,0.04l-6.47-11.3L76.4,67.42L76.4,67.42z M81.8,85.93l-6.09,10.64c-1.1,1.92-2.2,3.03-4.58,3.34l-7.08-0.09l0.12,4.12l-6.13-10.52l6.15-10.56l0.01,4.32l12.38-0.12 c1.17-0.01,2.09,0.01,3.24-0.31C80.52,86.54,81.17,86.26,81.8,85.93L81.8,85.93z M52.67,99.7l-11.76,0.02 c-2.6-0.4-4.27-1.81-4.77-3.77c-0.43-1.69-0.01-2.48,0.73-3.94c0.88-1.74,1.92-3.42,2.91-5.12l13.02,0.05L52.67,99.7L52.67,99.7z",
    ],
  },
  // Такое же переиспользование, как у самого Osmic - отдельной иконки для
  // "пункта утилизации" нет.
  waste_disposal: {
    viewBox: 122.88,
    originX: -6.7,
    fillRule: "evenodd",
    paths: [
      "M2.35,9.63h38.3V3.76C40.64,1.69,42.33,0,44.4,0h21.14c2.07,0,3.76,1.69,3.76,3.76v5.87h37.83 c1.29,0,2.35,1.06,2.35,2.35V23.5H0V11.98C0,10.69,1.05,9.63,2.35,9.63L2.35,9.63z M8.69,29.61h92.92c1.94,0,3.7,1.6,3.52,3.52 l-7.86,86.23c-0.18,1.93-1.59,3.52-3.52,3.52l-77.3,0c-1.93,0-3.35-1.59-3.52-3.52L5.17,33.13C4.99,31.2,6.75,29.61,8.69,29.61 L8.69,29.61L8.69,29.61z M33.93,95.11l-6.16-10.59c-1.11-1.92-1.53-3.42-0.6-5.64l3.62-6.09l-3.63-1.95l12.17-0.05l6.07,10.61 l-3.75-2.15l-6.08,10.78c-0.58,1.02-1.06,1.8-1.35,2.96C34.05,93.7,33.96,94.41,33.93,95.11L33.93,95.11z M36.38,62.36l5.86-10.2 c1.65-2.05,3.7-2.79,5.65-2.24c1.68,0.48,2.15,1.23,3.04,2.6c1.07,1.63,2,3.37,2.98,5.08l-6.55,11.26L36.38,62.36L36.38,62.36z M49.71,48.43l12.26-0.04c2.22-0.01,3.73,0.39,5.18,2.3l3.46,6.18l3.51-2.17l-6.04,10.56l-12.23-0.05l3.74-2.17l-6.3-10.66 c-0.6-1.01-1.03-1.81-1.89-2.65C50.88,49.23,50.31,48.81,49.71,48.43L49.71,48.43z M76.4,67.42l5.9,10.17 c0.95,2.45,0.57,4.6-0.89,6.01c-1.25,1.22-2.14,1.24-3.77,1.34c-1.95,0.11-3.92,0.05-5.89,0.04l-6.47-11.3L76.4,67.42L76.4,67.42z M81.8,85.93l-6.09,10.64c-1.1,1.92-2.2,3.03-4.58,3.34l-7.08-0.09l0.12,4.12l-6.13-10.52l6.15-10.56l0.01,4.32l12.38-0.12 c1.17-0.01,2.09,0.01,3.24-0.31C80.52,86.54,81.17,86.26,81.8,85.93L81.8,85.93z M52.67,99.7l-11.76,0.02 c-2.6-0.4-4.27-1.81-4.77-3.77c-0.43-1.69-0.01-2.48,0.73-3.94c0.88-1.74,1.92-3.42,2.91-5.12l13.02,0.05L52.67,99.7L52.67,99.7z",
    ],
  },
  spring: { viewBox: WATER_DROPLET_VIEWBOX, paths: [WATER_DROPLET_PATH] },
  ford: {
    viewBox: 14,
    paths: [
      "M 4.5,1 1,4 l 3.5,3 0,-2 5,0 0,2 3.5,-3 -3.5,-3 0,2 -5,0 z M 3,7.5 c -0.5338983,0 -1.4922852,1 -2,1 L 1,10 c 0.5371839,0 1.5254237,-1 2,-1 0.5042373,0 1.4661017,1 2,1 0.4755023,0 1.4922852,-1 2,-1 0.5042373,0 1.5217305,1 2,1 0.5042373,0 1.525424,-1 2,-1 0.474576,0 1.495763,1 2,1 l 0,-1.5 c -0.415254,0 -1.466102,-1.029661 -2,-1 -0.47827,0 -1.4652784,1 -2,1 -0.4449153,0 -1.4957627,-1 -2,-1 -0.5042373,0 -1.5540971,1 -2,1 -0.4755023,0 -1.4661017,-1 -2,-1 z m 0,3 c -0.5338983,0 -1.4922852,1 -2,1 L 1,13 c 0.5371839,0 1.5254237,-1 2,-1 0.5042373,0 1.4661017,1 2,1 0.4755023,0 1.4922852,-1 2,-1 0.5042373,0 1.5217305,1 2,1 0.5042373,0 1.525424,-1 2,-1 0.474576,0 1.495763,1 2,1 l 0,-1.5 c -0.415254,0 -1.466102,-1.029661 -2,-1 -0.47827,0 -1.4652784,1 -2,1 -0.4449153,0 -1.4957627,-1 -2,-1 -0.5042373,0 -1.5540971,1 -2,1 -0.4755023,0 -1.4661017,-1 -2,-1 z",
    ],
  },
  water_well: { viewBox: WATER_DROPLET_VIEWBOX, paths: [WATER_DROPLET_PATH] },
  firepit: {
    viewBox: 15,
    paths: [
      "M12 13.83L12 11.89L3 9.08L3 11.02L12 13.83ZM5.08 9.08C4.77 8.76 2.71 6.53 4.78 4.61C6.52 3 6.77 1.65 6.18 1C6.18 1 10.59 2.69 8 6.62C7.55 7.28 6.45 8.07 7.03 9.69L5.08 9.08L5.08 9.08ZM7.78 9.61C7.69 9.29 7.26 8.57 8.55 7.14C9.24 6.38 9.5 5.25 9.56 4.83C9.56 4.83 11.84 6.05 9.57 9.05L7.78 9.61L7.78 9.61ZM8.26 10.07L12 8.91L12 10.85L11.37 11.04L8.26 10.07ZM6.74 12.84L3 14L3 12.06L3.64 11.87L6.74 12.84Z",
    ],
  },
  // Не обведена по Osmic/Temaki, как соседние - простая рисованная вручную
  // кабинка на тросе: горизонтальная перекладина (трос), две подвески, бокс
  // (кабина). Достаточно проста, чтобы читаться в том же мелком размере.
  cable_car_station: {
    viewBox: 14,
    paths: [
      "M 1,1 L 13,1 L 13,2 L 1,2 Z " +
        "M 4.5,2 L 5.5,2 L 5.5,8 L 4.5,8 Z " +
        "M 8.5,2 L 9.5,2 L 9.5,8 L 8.5,8 Z " +
        "M 3,8 L 11,8 L 11,13 L 3,13 Z",
    ],
  },
  // Восемь рисованных вручную глифов ниже - та же оговорка «не обведены по
  // Osmic/Temaki», что и у cable_car_station выше, тот же простой вес
  // силуэта, чтобы читались в том же мелком размере.
  cave_entrance: {
    viewBox: 14,
    paths: ["M 7,1 C 4,1 2,4 2,7 L 2,13 L 12,13 L 12,7 C 12,4 10,1 7,1 Z"],
  },
  rock: {
    viewBox: 14,
    paths: [
      "M 2,13 C 1,11 1,9 3,8 C 2,6 4,3 6,4 C 7,2 10,2 10,5 C 12,5 13,7 11,9 C 13,10 12,13 10,13 Z",
    ],
  },
  // Настоящая иконка водопада Osmic (зеркалится на svgrepo.com под тем же
  // id), viewBox уже "0 0 14 14" - как у остальной таблицы, масштабировать
  // не пришлось.
  waterfall: {
    viewBox: 14,
    paths: [
      "m 0,1 0,1.25 c 1.9973598,0 2.9578431,0.3559307 3,2 L 3,6.5 c 0,2 1.5,3 2.5,3 0,0 -1,-1 -1,-3 l 0,-2.25 C 4.5,3.3890411 4.1949144,2.7403338 3.71875,2.25 4.9723257,2.25 5.9613912,2.7730892 6,4.25 L 6,6.5 c 0,2 1.5,3 2.5,3 0,0 -1,-1 -1,-3 l 0,-2.25 C 7.5,3.3355769 7.2575127,2.6919337 6.90625,2.25 8,2.25 9,2.8004763 9,4.25 L 9,6.5 c 0,2 1.5,3 2.5,3 0,0 -1,-1 -1,-3 l 0,-2.25 C 10.5,1.25 8,1 7,1 z m 3,9.5 c -0.5338983,0 -1.4922852,1 -2,1 L 1,13 c 0.5371839,0 1.5254237,-1 2,-1 0.5042373,0 1.4661017,1 2,1 0.4755023,0 1.4922852,-1 2,-1 0.5042373,0 1.5217305,1 2,1 0.5042373,0 1.525424,-1 2,-1 0.474576,0 1.495763,1 2,1 l 0,-1.5 c -0.415254,0 -1.466102,-1.029661 -2,-1 -0.47827,0 -1.4652784,1 -2,1 -0.4449153,0 -1.4957627,-1 -2,-1 -0.5042373,0 -1.5540971,1 -2,1 -0.4755023,0 -1.4661017,-1 -2,-1 z",
    ],
  },
  tower: {
    viewBox: 14,
    paths: [
      "M 6.3,0 L 7.7,0 L 7.7,3 L 9,3 L 9,4 L 8.4,4 L 10,13 L 11,13 L 11,14 L 3,14 L 3,13 L 4,13 L 5.6,4 L 5,4 L 5,3 L 6.3,3 Z",
    ],
  },
  // Готовая стоковая иконка вместо рисованного каркаса - детальный залитый
  // силуэт, а не обводка, поэтому `strokeWidth` здесь нет (обычная заливка,
  // как у остальных глифов таблицы). viewBox взят из исходного файла как
  // есть (511.653 - не круглое число, но ctx.scale() это не важно).
  power_tower: {
    viewBox: 511.653,
    paths: [
      "M431.827,490.32h-55.893L327.72,287.653h82.88c5.867,0,10.667-4.8,10.667-10.667c0-3.52-1.813-6.827-4.8-8.853l-64-42.667c-1.707-1.173-3.84-1.813-5.867-1.813h-21.333V138.32h74.773c5.867,0,10.667-4.8,10.667-10.667c0-3.52-1.813-6.933-4.8-8.853l-64-42.667c-1.707-1.173-3.84-1.813-5.973-1.813H309.8L265,4.56c-3.947-6.08-14.08-6.08-17.92,0l-45.12,69.76h-26.133c-2.133,0-4.16,0.64-5.867,1.813l-64,42.667c-4.907,3.307-6.187,9.92-2.987,14.827c2.027,2.987,5.333,4.8,8.853,4.8h74.667v85.333H165.16c-2.133,0-4.16,0.64-5.973,1.813l-64,42.667c-4.907,3.307-6.187,9.92-2.987,14.827c2.027,2.987,5.333,4.8,8.853,4.8h82.24L135.08,490.533H80.147c-5.333,0-10.133,3.84-10.88,9.067c-0.96,6.4,4.16,12.053,10.56,12.053h351.68c5.333,0,10.133-3.84,10.88-9.067C443.347,495.973,438.227,490.32,431.827,490.32z M345.427,453.733l-76.16-99.947l39.893-52.267L345.427,453.733z M286.867,223.653h-62.4l31.253-28.373L286.867,223.653z M271.613,180.667l32.213-29.12V210L271.613,180.667z M283.773,266.32h-56.64l17.707-21.333h21.867L283.773,266.32z M207.827,209.787v-58.133l32,29.12L207.827,209.787z M292.84,287.653L255.827,336.4L218.6,287.653H292.84z M343.293,244.987l32,21.333H311.08l-17.067-21.333H343.293z M332.627,95.653l32,21.333h-56.32l-21.333-21.333H332.627z M255.827,30.053L284.2,74.32h-56.853L255.827,30.053z M278.12,116.987h-44.48l21.333-21.333h1.813L278.12,116.987z M286.867,138.32l-31.147,28.16l-30.933-28.16H286.867z M147.027,116.987l32-21.333h45.653l-21.333,21.333H147.027z M136.36,266.32l32-21.333h48.747L199.4,266.32H136.36z M202.067,300.88l40.32,52.907L165.373,454.8L202.067,300.88z M165.373,490.32l90.453-118.72l90.453,118.72H165.373z",
    ],
  },
  bench: {
    viewBox: 14,
    paths: [
      "M 1,3 L 2.5,3 L 2.5,6 L 1,6 Z " +
        "M 1,6 L 13,6 L 13,7.5 L 1,7.5 Z " +
        "M 1,7.5 L 2.5,7.5 L 2.5,14 L 1,14 Z " +
        "M 11.5,7.5 L 13,7.5 L 13,14 L 11.5,14 Z",
    ],
  },
  // Настоящая иконка Osmic "picnic-site", viewBox уже "0 0 15 15" - как и у
  // ряда других видов в этой таблице, использована как есть.
  picnic_table: {
    viewBox: 15,
    paths: [
      "M4,3C3.446,3,3,3.446,3,4s0.446,1,1,1h1.2969L4.6523,7H2.5c-0.554,0-1,0.446-1,1s0.446,1,1,1h1.5098L3.041,12.0098c-0.1284,0.3939,0.0868,0.8173,0.4807,0.9457s0.8173-0.0868,0.9457-0.4807c0.0005-0.0013,0.0009-0.0027,0.0013-0.004L5.5859,9h3.8281l1.1172,3.4707c0.1273,0.3943,0.5501,0.6107,0.9443,0.4834s0.6107-0.5501,0.4834-0.9443l0,0L10.9902,9H12.5c0.554,0,1-0.446,1-1s-0.446-1-1-1h-2.1523L9.7031,5H11c0.554,0,1-0.446,1-1s-0.446-1-1-1H4z M6.873,5H8.127l0.6445,2h-2.543L6.873,5z",
    ],
  },
  // Подпути точки/стержня буквы «i» намотаны в обратную сторону относительно
  // внешнего квадрата (nonzero fill rule, дефолт canvas в
  // buildSvgPathIconImage) - так они пробивают дыру, а не просто закрашивают
  // сверху тем же цветом.
  information: {
    viewBox: 14,
    paths: [
      "M 3,1 L 11,1 C 12,1 13,2 13,3 L 13,11 C 13,12 12,13 11,13 L 3,13 C 2,13 1,12 1,11 L 1,3 C 1,2 2,1 3,1 Z " +
        "M 6.3,3.3 L 6.3,4.7 L 7.7,4.7 L 7.7,3.3 Z " +
        "M 6.3,6 L 6.3,11 L 7.7,11 L 7.7,6 Z",
    ],
  },
};

// Только альфа-канал (без запечённой обводки/ореола) - `sdf: true` тонирует
// один и тот же битмап на объект через icon-color, а icon-halo-color/
// icon-halo-width слоя pois-icon (SDF-only пара) рисуют обводку при рендере.
//
// `strokeWidth` переключает с залитых силуэтов (все глифы, кроме
// power_tower) на открытую обводку - пути решётчатой опоры это буквальные
// отрезки (рельсы, раскосы), не замкнутые контуры фигуры, заливка потребовала
// бы выразить каждый сегмент тонким четырёхугольником. Скруглённые
// соединения/концы, чтобы стыки рельс-раскос не давали разрывов на этой
// толщине обводки.
export const buildSvgPathIconImage = (
  viewBox: number,
  paths: string[],
  strokeWidth?: number,
  originX = 0,
  originY = 0,
  fillRule: CanvasFillRule = "nonzero",
): ImageData | null => {
  // 32, не 24 (peak/pass/information остаются на 24) - детальные пиктограммы
  // (WC, урна) теряли тонкие штрихи при растеризации; заодно крупнее.
  const size = 32;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.scale(size / viewBox, size / viewBox);
  if (originX || originY) ctx.translate(-originX, -originY);
  if (strokeWidth) {
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = strokeWidth;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    for (const d of paths) ctx.stroke(new Path2D(d));
  } else {
    ctx.fillStyle = "#000000";
    for (const d of paths) ctx.fill(new Path2D(d), fillRule);
  }
  return ctx.getImageData(0, 0, size, size);
};

export const poiIconImageId = (kind: string) => `osmic-poi-${kind}`;

// Несколько видов намеренно рисуют один и тот же глиф (все пять водных видов
// делят WATER_DROPLET_PATH, waste_basket/waste_disposal всегда так делали) -
// кластерный спрайт дедуплицирует их по этой сигнатуре.
export const poiGlyphSignature = (kind: string): string => {
  const spec = POI_ICON_SVG_PATHS[kind];
  if (!spec) return kind;
  // originX/originY/fillRule included - different offset/rule means a
  // different rendered glyph even with the same viewBox/paths.
  return `${spec.viewBox}::${spec.strokeWidth ?? 0}::${spec.originX ?? 0}::${spec.originY ?? 0}::${spec.fillRule ?? "nonzero"}::${spec.paths.join("|")}`;
};

// Тот же значок POI, но inline-SVG для DOM-меток (выбранная точка поверх
// всех слоёв, см. useSearchLayers.ts). null - у вида нет значка.
export const buildPoiIconSvg = (kind: string, color: string = POI_COLOR): string | null => {
  const spec = POI_ICON_SVG_PATHS[kind];
  if (!spec) return null;
  const viewBox = `${spec.originX ?? 0} ${spec.originY ?? 0} ${spec.viewBox} ${spec.viewBox}`;
  const paint = spec.strokeWidth
    ? `fill="none" stroke="${color}" stroke-width="${spec.strokeWidth}" stroke-linecap="round" stroke-linejoin="round"`
    : `fill="${color}" fill-rule="${spec.fillRule ?? "nonzero"}"`;
  const paths = spec.paths.map((d) => `<path d="${d}" ${paint}/>`).join("");
  return `<svg viewBox="${viewBox}" width="18" height="18" aria-hidden="true">${paths}</svg>`;
};
